import { motion } from "framer-motion";
import HologramFigure from "@/components/sections/smithers/HologramFigure";
import HudFrame from "@/components/primitives/HudFrame";
import GhostLink from "@/components/primitives/GhostLink";
import BlueprintBackdrop from "@/components/motion/BlueprintBackdrop";

export default function SmithersSection() {
    return (
        <section
            id="smithers"
            data-testid="smithers-section"
            className="relative bg-tca-deep overflow-hidden"
        >
            {/* Ambient blueprint imprint (extra faint) */}
            <BlueprintBackdrop
                src="/brand/blueprint-01.jpg"
                opacity={0.045}
                parallaxRange={90}
                rotateRange={-1.3}
            />

            <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-28 sm:py-40">
                <div className="grid grid-cols-12 gap-10 lg:gap-12 items-center">
                    {/* Left: copy */}
                    <div className="col-span-12 lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.6 }}
                            className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.36em] text-tca-cyan/85 mb-5 flex items-center gap-3"
                            data-testid="smithers-eyebrow"
                        >
                            <span className="inline-block h-px w-8 bg-tca-cyan/50" />
                            // The Guidance Layer
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
                            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="smithers-headline"
                            className="font-display text-[42px] leading-[1.02] sm:text-[64px] sm:leading-[0.98] lg:text-[88px] lg:leading-[0.96] tracking-[-0.025em] text-tca-ink"
                        >
                            Meet Smithers.
                        </motion.h2>

                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="font-display italic neon-cyan text-xl sm:text-2xl mt-3"
                        >
                            Disciplined execution presence.
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7, delay: 0.3 }}
                            className="mt-7 sm:mt-9 text-tca-ink-muted text-base sm:text-lg leading-relaxed max-w-xl"
                        >
                            Smithers is not a chatbot. Smithers is the disciplined
                            execution layer of the ecosystem — a guidance presence
                            trained on doctrine. Smithers helps you move with structure,
                            validate decisions, and execute with intention.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7, delay: 0.45 }}
                            className="mt-10"
                        >
                            <GhostLink href="#faq" data-testid="smithers-cta">
                                Learn about the ecosystem
                            </GhostLink>
                        </motion.div>
                    </div>

                    {/* Right: hologram */}
                    <div className="col-span-12 lg:col-span-5 relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.92, rotateY: -10 }}
                            whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="smithers-hologram-wrap"
                        >
                            <HudFrame
                                bracketColor="var(--tca-cyan-core)"
                                bracketSize={16}
                                borderTint="rgba(0, 224, 232, 0.18)"
                                className="bg-tca-bg-panel/40 backdrop-blur-md"
                                innerClassName="p-6 sm:p-8 flex flex-col items-center gap-4"
                            >
                                <HologramFigure />
                                <div className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-tca-cyan/85 text-center">
                                    // Smithers XI® · Ecosystem Layer
                                </div>
                                {/* HUD readout strip */}
                                <div className="w-full grid grid-cols-3 gap-3 mt-2 font-mono-tight text-[9px] uppercase tracking-[0.2em] text-tca-ink-muted/80">
                                    <div className="text-center border-l border-tca-cyan/30 pl-2">
                                        <div className="text-tca-cyan/85">Status</div>
                                        <div className="text-tca-ink/85">Online</div>
                                    </div>
                                    <div className="text-center border-l border-tca-cyan/30 pl-2">
                                        <div className="text-tca-cyan/85">Uplink</div>
                                        <div className="text-tca-ink/85">Stable</div>
                                    </div>
                                    <div className="text-center border-l border-tca-cyan/30 pl-2">
                                        <div className="text-tca-cyan/85">Doctrine</div>
                                        <div className="text-tca-ink/85">Active</div>
                                    </div>
                                </div>
                            </HudFrame>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
